import {
  DocumentIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import type { ProjectStatus, ProjectPhase } from "@/features/Projects";

export const PROJECT_STATUS_CONFIG: Record<
  ProjectStatus,
  {
    label: string;
    color: string;
    icon: typeof DocumentIcon;
  }
> = {
  draft: {
    label: "Em Elaboração",
    color: "bg-yellow-100 text-yellow-800",
    icon: DocumentIcon,
  },
  planning: {
    label: "Planejamento",
    color: "bg-blue-100 text-blue-800",
    icon: ClockIcon,
  },
  in_progress: {
    label: "Em Andamento",
    color: "bg-primary-100 text-primary-800",
    icon: ClockIcon,
  },
  completed: {
    label: "Concluído",
    color: "bg-green-100 text-green-800",
    icon: CheckCircleIcon,
  },
  cancelled: {
    label: "Cancelado",
    color: "bg-red-100 text-red-800",
    icon: XCircleIcon,
  },
};

export const PROJECT_PHASE_CONFIG: Record<
  ProjectPhase,
  {
    label: string;
    description: string;
    progress: number;
  }
> = {
  description: {
    label: "Descrição",
    description: "Descrição detalhada processada pela IA",
    progress: 20,
  },
  objectives: {
    label: "Objetivos",
    description: "Definição dos objetivos SMART",
    progress: 25,
  },
  technical_solution: {
    label: "Solução Técnica",
    description: "Arquitetura e tecnologias",
    progress: 35,
  },
  milestones: {
    label: "Marcos",
    description: "Principais entregas e prazos",
    progress: 45,
  },
  stories: {
    label: "Histórias",
    description: "User stories e critérios de aceitação",
    progress: 55,
  },
  schema: {
    label: "Schema",
    description: "Estrutura de APIs, UI e dados",
    progress: 65,
  },
  resources: {
    label: "Recursos",
    description: "Equipe e custos estimados",
    progress: 75,
  },
  proposal: {
    label: "Proposta",
    description: "Proposta comercial final",
    progress: 85,
  },
  execution: {
    label: "Execução",
    description: "Desenvolvimento em andamento",
    progress: 100,
  },
};

export function getStatusIcon(status: ProjectStatus) {
  return PROJECT_STATUS_CONFIG[status].icon;
}

export function getPhaseColor(phase: ProjectPhase): string {
  const progress = PROJECT_PHASE_CONFIG[phase].progress;

  if (progress <= 20) return "bg-red-500";
  if (progress <= 40) return "bg-yellow-500";
  if (progress <= 60) return "bg-blue-500";
  if (progress <= 80) return "bg-primary-500";
  return "bg-green-500";
}

export function getProjectProgress(phase: ProjectPhase): number {
  const phaseConfig = PROJECT_PHASE_CONFIG[phase];
  
  if (!phaseConfig) {
    console.warn(`Phase config not found for phase: ${phase}`);
    return 0; // Fallback para progresso 0
  }
  
  return phaseConfig.progress;
}

export function formatDate(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const minutes = Math.floor(diff / (1000 * 60));
      return minutes <= 1 ? "Agora" : `${minutes}m atrás`;
    }
    return hours === 1 ? "1h atrás" : `${hours}h atrás`;
  }

  if (days === 1) return "Ontem";
  if (days < 7) return `${days}d atrás`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return weeks === 1 ? "1 sem atrás" : `${weeks} sem atrás`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return months === 1 ? "1 mês atrás" : `${months} meses atrás`;
  }

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export function truncateDescription(description: string, maxLength: number = 120): string {
  if (!description) return "";
  
  if (description.length <= maxLength) {
    return description;
  }

  // Trunca no último espaço para não cortar palavras
  const truncated = description.substring(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  
  if (lastSpaceIndex > maxLength * 0.8) {
    return truncated.substring(0, lastSpaceIndex) + "...";
  }
  
  return truncated + "...";
}